import {Button, ButtonStyle} from '@mintdata-wsp/common/components/button/Button';
import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {ScrollBarShadows} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SpreadsheetTabsState';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {IDeleteDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/DeleteDialog';
import {ErrorTabSheetDialog} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/duplicateNameTabSheetDialog/ErrorTabSheetDialog';
import {
    ScrollTypes,
    ScrollZone
} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/scrollZone/ScrollZone';
import {SpreadsheetTab} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/SpreadsheetTab';
import {
    ISwitcherItemData,
    Switcher,
    SwitcherConstructor
} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/switcher/Switcher';
import {SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {DataSource, IListItemState} from '@mintdata/mint-kit/api/models/List';
import {
    ContextMenu,
    ContextMenuConstructor,
    IContextMenuItem,
    ToggleOn
} from '@mintdata/mint-kit/impl/components/contextMenu/ContextMenu';
import {PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {ImgIcons} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcons';
import {IStyles, Styles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, computed} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import ResizeSensor from 'css-element-queries/src/ResizeSensor';

const tabsPanelStyles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/SpreadsheetTabsView.less');

enum Actions
{
    AddClientTab = 'addClientTab',
    AddCloudTab = 'addCloudTab',
    ActiveTab = 'activeTab',
    HideDeletionMessage = 'hideDeletionMessage',
    DeleteTab = 'deleteTab',
    ScrollLeft = 'scrollLeft',
    ScrollRight = 'scrollRight',
}

enum ScrollDirection
{
    Left = 'left',
    Right = 'right'
}

enum TabMovingDirection
{
    Left = 'left',
    Right = 'right'
}

export interface ITabsPanelProps
{
    workbookId:WorkbookId;
    theme?:IStyles;
}

interface ITabsPanelState
{
    styles:IStyles;
}

@observer
export class SpreadsheetTabsView extends React.Component<ITabsPanelProps, ITabsPanelState>
{
    private _tabsContainerRef:HTMLElement;
    private _resizeSensor:ResizeSensor;
    private _scrollZoneRef:ScrollZone;
    private _lastEditorRef:HTMLDivElement;
    private _wasAddButtonClickedWithAlt:boolean = false;

    public componentWillMount():void
    {
        const {theme} = this.props;
        this._initStyles(theme);
    }

    public componentWillReceiveProps(newProps:ITabsPanelProps):void
    {
        const {theme} = this.props;

        if( theme !== newProps.theme )
            this._initStyles(newProps.theme);
    }

    private _initStyles(theme:IStyles):void
    {
        this.setState({
            styles: Styles.mergeStyles(tabsPanelStyles, theme || {})
        });
    }

    @computed
    private get _switcherDataSource():Array<ISwitcherItemData<AnySheet>>
    {
        const {workbookId} = this.props;

        const workbook:Workbook = controllers.ui.spreadsheets.main.findWorkbookById(workbookId);

        return workbook.runtime.sheets.map((sheet:AnySheet) =>
            ({
                id: sheet.id,
                isSelected: controllers.ui.spreadsheets.tabs.isActiveSheet(workbookId, sheet.id),
                value: sheet
            }));
    }

    @bind
    public activateTab(workbookId:WorkbookId, sheetId:SheetId):void
    {
        const {main, tabs, mesh, workbook} = controllers.ui.spreadsheets;

        const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(sheetId);

        const editorState = main.getEditorState();

        if( editorState.isEditMode && !(editorState.input.isSelectCellRefMode || editorState.input.isSelectCellRefRangeMode))
            mesh.commitEditingCellIfValid(ctx);

        tabs.setActiveSheet(workbookId, sheetId);

        const index = workbook.findSheetIndex(workbookId, sheetId);
        this._scrollToVisibleTab(index);

        mesh.focusMesh(ctx);
        mesh.onFocusMesh(ctx);
    }

    @bind
    private _deleteTab(workbookId:WorkbookId, sheetId:SheetId):void
    {
        controllers.ui.dialogs.showDialog(Dialogs.Delete, false, {
            message: 'Are you sure you want to delete this sheet?',
            onClose: () => this._actionHandler(workbookId, Actions.HideDeletionMessage, sheetId),
            onClickDelete: () => this._actionHandler(workbookId, Actions.DeleteTab, sheetId)
        } as IDeleteDialogProps);
    }

    @bind
    private _scrollToVisibleTab(tabIndex:number):void
    {
        const tabsElements = this._getTabsElements();
        const tab = tabsElements[tabIndex];

        if( tab && this._scrollZoneRef )
        {
            const scrollZone = this._scrollZoneRef;
            const scrollContainerOptions = scrollZone.getScrollContainerOptions();
            const tabLeftPosition = tab.offsetLeft;

            if( tabLeftPosition < scrollContainerOptions.scrollLeft )
                scrollZone.scrollTo({
                    left: tab.offsetLeft,
                    behavior: 'smooth'
                });
            else if( tabLeftPosition + tab.offsetWidth > scrollContainerOptions.scrollLeft )
                scrollZone.scrollTo({
                    left: tab.offsetLeft - scrollContainerOptions.width + tab.offsetWidth,
                    behavior: 'smooth'
                });
        }
    }

    @action.bound
    private _actionHandler(workbookId:WorkbookId, tabAction:Actions, sheetId?:SheetId):void
    {
        const wc = controllers.ui.spreadsheets.workbook;

        const workbook:Workbook = controllers.ui.spreadsheets.main.findWorkbookById(workbookId);
        const activeSheet = controllers.ui.spreadsheets.tabs.getActiveSheet(workbookId);
        const sheet = wc.findSheetById(workbookId, sheetId);

        switch( tabAction )
        {
            case Actions.AddClientTab:
            case Actions.AddCloudTab:
            {
                const activeSheetIndex = wc.findSheetIndex(workbookId, activeSheet.id);
                const newSheetIndex = activeSheetIndex + 1;
                const newSheetName = wc.getNextSheetName(workbookId);

                const newSheet:AnySheet = tabAction === Actions.AddCloudTab
                    ? wc.createNewCloudSheet(workbook, newSheetName)
                    : wc.createNewSheet(workbook, newSheetName);

                wc.insertSheet(workbookId, newSheetIndex, newSheet);
                this.activateTab(workbookId, newSheet.id);

                break;
            }
            case Actions.HideDeletionMessage:
            {
                controllers.ui.dialogs.hideDialog(Dialogs.Delete);
                break;
            }
            case Actions.DeleteTab:
            {
                controllers.ui.dialogs.hideDialog(Dialogs.Delete);

                if( sheet.id === activeSheet.id )
                {
                    let sheetIndex:number = wc.findSheetIndex(workbookId, activeSheet.id);

                    if( sheetIndex === 0 )
                        sheetIndex++;
                    else
                        sheetIndex--;

                    const newActiveSheet:AnySheet = wc.findSheetByIndex(workbookId, sheetIndex);
                    this.activateTab(workbookId, newActiveSheet.id);
                }

                setTimeout(() =>
                {
                    wc.removeSheet(workbookId, sheet.id);
                });

                break;
            }
            case Actions.ActiveTab:
            {
                this.activateTab(workbookId, sheet.id);
                break;
            }
            case Actions.ScrollLeft:
            {
                this._scrollTabs(ScrollDirection.Left);
                break;
            }
            case Actions.ScrollRight:
            {
                this._scrollTabs(ScrollDirection.Right);
                break;
            }
            default:
                throw new Error(`Unknown action: ${tabAction}`);
        }
    }

    private _getTabsElements():HTMLElement[]
    {
        const {styles} = this.state;
        const tabsContainer = this._tabsContainerRef;

        return tabsContainer ? tabsContainer.querySelectorAll(`.${styles.tabContainer}`) as any : [];
    }

    private _scrollTabs(direction:ScrollDirection)
    {
        if( this._scrollZoneRef )
        {
            const scrollContainerOptions = this._scrollZoneRef.getScrollContainerOptions();
            const tabs = this._getTabsElements();
            let scrollableTab:HTMLElement;

            for( let i = 0; i < tabs.length; i++ )
            {
                const tab = tabs[i];
                let tabPosition:number;
                let scrollVisiblePosition:number;

                if( direction === ScrollDirection.Left )
                {
                    tabPosition = (tab.offsetLeft);
                    scrollVisiblePosition = scrollContainerOptions.scrollLeft - scrollContainerOptions.width;
                }
                else
                {
                    tabPosition = (tab.offsetLeft) + tab.offsetWidth;
                    scrollVisiblePosition = scrollContainerOptions.scrollLeft + scrollContainerOptions.width;
                }

                if( tabPosition > scrollVisiblePosition )
                {
                    scrollableTab = tab;
                    break;
                }
            }

            if( scrollableTab )
                this._scrollZoneRef.scrollTo({
                    left: scrollableTab.offsetLeft,
                    behavior: 'smooth'
                });
        }
    }

    private _renderControls():JSX.Element
    {
        const {styles} = this.state;
        const cx = classnames.bind(styles);

        const addSheetMenuButtonId = 'addSheetMenuButtonId';
        const sheetsMenuButtonId = 'sheetsMenuButton';
        const TypedContextMenu:ContextMenuConstructor<any> = ContextMenu as any;

        const {workbookId} = this.props;
        const tabState = controllers.ui.spreadsheets.tabs.getTabState(workbookId);
        const showShadow = tabState.scrollBarShadows.left;

        return (
            <div className={cx(styles.controls, showShadow && styles.controlsShadow)}>
                <div className={styles.control} onClickCapture={this._handleAddControlClickCapture}>
                    <TypedContextMenu
                        toggleOn={[ToggleOn.CLICK]}
                        togglersIds={[addSheetMenuButtonId]}
                        popupAnchor={PopupAnchor.LeftBottom}
                        sourceAnchor={SourceAnchor.LeftTop}
                        dataSource={this._addSheetMenuProvider}
                    >
                        <Button
                            id={addSheetMenuButtonId}
                            style={ButtonStyle.SimpleSecondary}
                            icon={MDIcons.common.Add}
                            iconSize={MDIconSize.medium}
                            className={styles.button}
                        />
                    </TypedContextMenu>
                </div>
                <div className={styles.control}>
                    <TypedContextMenu
                        toggleOn={[ToggleOn.CLICK]}
                        togglersIds={[sheetsMenuButtonId]}
                        theme={{list: styles.sheetsList}}
                        itemRenderer={this._renderSheetsMenuItem}
                        popupAnchor={PopupAnchor.LeftBottom}
                        sourceAnchor={SourceAnchor.LeftTop}
                        dataSource={this._sheetsMenuProvider}
                    >
                        <Button
                            id={sheetsMenuButtonId}
                            style={ButtonStyle.SimpleSecondary}
                            icon={MDIcons.common.Menu}
                            iconSize={MDIconSize.medium}
                            className={styles.button}
                        />
                    </TypedContextMenu>
                </div>
            </div>
        );
    }

    private _handleAddControlClickCapture = (event:React.MouseEvent<HTMLElement>) =>
    {
        this._wasAddButtonClickedWithAlt = event.altKey;
    };

    @bind
    private _addSheetMenuProvider():DataSource<IContextMenuItem>
    {
        const {workbookId} = this.props;

        let result:DataSource<IContextMenuItem> = [{
            label: 'Sheet',
            handler: () => this._actionHandler(workbookId, Actions.AddClientTab),
            icon: this._wasAddButtonClickedWithAlt ? ImgIcons.empty : null
        }];

        if( this._wasAddButtonClickedWithAlt )
        {
            result.push({
                label: '',
                isSeparator: true
            });
            result.push({
                label: 'Cloud Sheet',
                handler: () => this._actionHandler(workbookId, Actions.AddCloudTab),
                icon: ImgIcons.cloud
            });
        }

        return result;
    }

    @bind
    private _renderSheetsMenuItem({label}:IContextMenuItem, {isSelected}:IListItemState, {menuItem}:IStyles):JSX.Element
    {
        const {styles} = this.state;
        const cx = classnames.bind(styles);

        return (
            <div className={cx(styles.sheetsMenuItem, menuItem)}>
                <div className={styles.sheetsMenuItemIcon}>
                    {isSelected && <MDIcon name={MDIcons.common.Checkmark} size={MDIconSize.medium16} />}
                </div>
                <div className={styles.sheetsMenuItemLabel}>{label}</div>
            </div>
        );
    }

    @bind
    private _sheetsMenuProvider():DataSource<IContextMenuItem>
    {
        const workbookController = controllers.ui.spreadsheets.workbook;
        const {workbookId} = this.props;

        const activeSheet = controllers.ui.spreadsheets.tabs.getActiveSheet(workbookId);
        const allSheets = workbookController.getAllSheets(workbookId);

        return allSheets.map((sheet:AnySheet) =>
        {
            const {id, name} = sheet;
            const isSelected = (id === activeSheet.id);

            return {
                isSelected,
                label: name,
                handler: () => this.activateTab(workbookId, sheet.id)
            } as IContextMenuItem;
        });
    }

    @bind
    private _renderScrollControls():JSX.Element
    {
        const {styles} = this.state;
        const cx = classnames.bind(styles);
        const {workbookId} = this.props;
        const tabState = controllers.ui.spreadsheets.tabs.getTabState(workbookId);
        const showShadow = tabState.scrollBarShadows.right;

        return (
            <div className={cx(styles.scrollControls, showShadow && styles.controlsShadow)}>
                <Button
                    style={ButtonStyle.SimpleSecondary}
                    icon={MDIcons.common.ArrowDropright}
                    iconSize={MDIconSize.medium}
                    className={cx(styles.scrollButtonLeft, styles.button)}
                    onClick={() => this._actionHandler(workbookId, Actions.ScrollLeft)}
                />
                <Button
                    style={ButtonStyle.SimpleSecondary}
                    icon={MDIcons.common.ArrowDropright}
                    iconSize={MDIconSize.medium}
                    className={styles.button}
                    onClick={() => this._actionHandler(workbookId, Actions.ScrollRight)}
                />
            </div>
        );
    }

    @bind
    private _onEditorRef(element:HTMLDivElement):void
    {
        this._lastEditorRef = element;
    }

    private _renderTab(sheet:AnySheet):JSX.Element
    {
        const {theme, workbookId} = this.props;
        const {styles} = this.state;
        const tabState = controllers.ui.spreadsheets.tabs.getTabState(workbookId);

        const wc = controllers.ui.spreadsheets.workbook;
        const sheetIndex = wc.findSheetIndex(workbookId, sheet.id);

        const {dndTargetSheetIndex, dndSourceSheetTabOffset} = tabState;
        const tabPlaceholder = (sheetIndex === dndTargetSheetIndex || (dndTargetSheetIndex === -1 && sheetIndex === 0))
            ? (<div style={{width: dndSourceSheetTabOffset.width}} />)
            : null;
        const showBefore = (dndTargetSheetIndex === -1 && sheetIndex === 0);

        const activeSheet = controllers.ui.spreadsheets.tabs.getActiveSheet(workbookId);
        const activeSheetIndex = activeSheet ? wc.findSheetIndex(workbookId, activeSheet.id) : -1;

        const indexToSelect = activeSheetIndex;
        const isSelected = (sheetIndex === indexToSelect);

        return (
            <div className={styles.tabContainer} data-component={isSelected ? 'active-sheet' : 'sheet'}>
                {showBefore && tabPlaceholder}
                <SpreadsheetTab
                    workbookId={workbookId}
                    sheet={sheet}
                    isSelected={isSelected}
                    theme={theme}
                    onEditorRef={this._onEditorRef}
                    onActivateTab={this.activateTab}
                    onDeleteTab={this._deleteTab}
                />
                {!showBefore && tabPlaceholder}
            </div>
        );
    }

    private _renderTabs():JSX.Element
    {
        const {styles} = this.state;
        const TypedSwitcher:SwitcherConstructor<ISwitcherItemData<AnySheet>> = Switcher as any;
        const {workbookId} = this.props;
        const tabState = controllers.ui.spreadsheets.tabs.getTabState(workbookId);
        const {isInProcess} = tabState;

        return (
            <div
                ref={(ref) =>
                {
                    this._tabsContainerRef = ref;
                }}
                className={styles.tabsContainer}
            >
                <ScrollZone
                    ref={(ref) =>
                    {
                        this._scrollZoneRef = ref;
                    }}
                    active={isInProcess}
                    type={ScrollTypes.Horizontal}
                    onScroll={() =>
                    {
                        this._identifyScrollBarShadows();
                    }}
                >
                    <TypedSwitcher
                        itemRenderer={({value}) => this._renderTab(value)}
                        dataSource={this._switcherDataSource}
                        onItemSelect={({value}) => this._actionHandler(workbookId, Actions.ActiveTab, value.id)}
                    />
                </ScrollZone>
            </div>
        );
    }

    private _identifyScrollBarShadows():void
    {
        const {workbookId} = this.props;
        const tabsController = controllers.ui.spreadsheets.tabs;
        const tabState = tabsController.getTabState(workbookId);

        const {isScrollableTabs, scrollBarShadows} = tabState;

        if( this._scrollZoneRef )
        {
            const scrollContainerOptions = this._scrollZoneRef.getScrollContainerOptions();
            const scrollShadows:ScrollBarShadows = isScrollableTabs
                ? {
                    left: scrollContainerOptions.scrollLeft > 0,
                    right: (scrollContainerOptions.scrollLeft + scrollContainerOptions.width) < scrollContainerOptions.scrollWidth
                }
                : {left: false, right: false};

            if( !(scrollShadows.left === scrollBarShadows.left && scrollShadows.right === scrollBarShadows.right) )
                tabsController.setTabsScrollBarShadows(workbookId, scrollShadows);
        }
    }

    @bind
    private _identifyScrollBar():void
    {
        const {workbookId} = this.props;

        requestAnimationFrame(() =>
        {
            if( this._scrollZoneRef && this._tabsContainerRef )
            {
                const scrollContainerOptions = this._scrollZoneRef.getScrollContainerOptions();
                const tabsContainer = this._tabsContainerRef;
                const isScrollable = (scrollContainerOptions.width < scrollContainerOptions.scrollWidth);

                const tabsController = controllers.ui.spreadsheets.tabs;
                tabsController.setIsScrollableTabs(workbookId, isScrollable);

                this._identifyScrollBarShadows();

                tabsController.setPanelOffset(workbookId, tabsContainer.getBoundingClientRect());
            }
        });
    }

    public componentDidMount():void
    {
        this._resizeSensor = new ResizeSensor(this._tabsContainerRef, () =>
        {
            this._identifyScrollBar();

            const wc = controllers.ui.spreadsheets.workbook;
            const {workbookId} = this.props;
            const activeSheet = controllers.ui.spreadsheets.tabs.getActiveSheet(workbookId);
            const activeSheetIndex = wc.findSheetIndex(workbookId, activeSheet.id);

            this._scrollToVisibleTab(activeSheetIndex);
        });

        this._identifyScrollBar();
    }

    public componentDidUpdate():void
    {
        this._identifyScrollBar();
    }

    public componentWillUnmount():void
    {
        if( this._resizeSensor )
        {
            this._resizeSensor.detach(this._identifyScrollBar);
            this._resizeSensor = null;
        }

        const {workbookId} = this.props;
        const tabsController = controllers.ui.spreadsheets.tabs;

        if( controllers.ui.openDesign.getOpenDesign() )
            tabsController.setIsScrollableTabs(workbookId, false);
    }

    @bind
    private _renderDuplicateNameMessage():JSX.Element
    {
        const {workbookId} = this.props;
        const {editorSheetName} = controllers.ui.spreadsheets.tabs.getTabState(workbookId);
        const message = `A sheet with the name "${editorSheetName}" already exists. Please enter another name.`;
        return this.renderNameErrorMessage(message);
    }

    @bind
    private _renderInvalidNameMessage():JSX.Element
    {
        const {workbookId} = this.props;
        const {editorSheetName} = controllers.ui.spreadsheets.tabs.getTabState(workbookId);
        let message = `Sheet name "${editorSheetName}" is invalid.`;
        if( !editorSheetName || editorSheetName.length === 0 )
            message = `Sheet name can't be empty`;
        return this.renderNameErrorMessage(message);
    }

    @bind
    private renderNameErrorMessage(message:string):JSX.Element
    {
        return (
            <ErrorTabSheetDialog
                message={message}
                onClose={this._handleRenameErrorConfirmation}
                onClickSuccess={this._handleRenameErrorConfirmation}
            />
        );
    }

    @bind
    private _handleRenameErrorConfirmation():void
    {
        const {workbookId} = this.props;
        const tabsController = controllers.ui.spreadsheets.tabs;
        tabsController.disableEditorErrors(workbookId);

        if( this._lastEditorRef )
            this._lastEditorRef.focus();
    }

    public render():JSX.Element
    {
        const {styles} = this.state;
        const {workbookId} = this.props;
        const tabState = controllers.ui.spreadsheets.tabs.getTabState(workbookId);
        const {isScrollableTabs, editorErrorsAllowed, isEditorSheetNameInvalid, isEditorSheetNameDuplicate} = tabState;

        return (
            <div className={styles.base}>
                {this._renderControls()}
                {this._renderTabs()}
                {isScrollableTabs && this._renderScrollControls()}
                {editorErrorsAllowed && isEditorSheetNameDuplicate && this._renderDuplicateNameMessage()}
                {editorErrorsAllowed && isEditorSheetNameInvalid && this._renderInvalidNameMessage()}
            </div>
        );
    }
}
